import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from '../icons';
import {
  WEEKDAY_LABELS,
  addDays,
  buildMonthGrid,
  compareDates,
  formatLongDate,
  monthLabel,
  parseIsoDate,
  stepMonth,
  toIsoDate,
  type CalendarCell,
} from './dateGrid';

/**
 * The open-state calendar grid popover (first-run-setup.md SS5.3-5.6).
 * A full Monday-first month grid, not a native date input or a segmented
 * text field — see that spec's SS5.1 for the rationale (seeing the date in
 * context matters for a date-realism-forecast-feeding target date).
 */
export function DatePickerPopover({
  selected,
  today,
  anchor = 'below',
  onSelect,
  onClose,
}: {
  readonly selected: string | null;
  readonly today: string;
  /**
   * Which side of the positioned ancestor the dialog opens toward
   * (today-phone-parity.md §5.1). `'below'` (the default) preserves this
   * component's original desktop behavior byte-for-byte — every existing
   * caller (`TodayHeaderDesktopV2`, `ObjectiveFormScreen`, `ActionsTable`)
   * is unaffected. `'above'` is for anchors pinned to the viewport bottom
   * with no room below (the phone footer menu) — swaps `top` for `bottom`
   * on the outer positioned wrapper only; the grid/header/footer content
   * inside is identical either way.
   */
  readonly anchor?: 'below' | 'above';
  readonly onSelect: (date: string) => void;
  readonly onClose: () => void;
}) {
  const initial = parseIsoDate(selected ?? today);
  const [viewYear, setViewYear] = useState(initial.year);
  const [viewMonth, setViewMonth] = useState(initial.month);
  const [focusedDate, setFocusedDate] = useState(selected ?? today);
  const [announcement, setAnnouncement] = useState('');

  const gridRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const weeks = useMemo(() => {
    const rows: CalendarCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [cells]);

  function goToMonth(year: number, month: number) {
    setViewYear(year);
    setViewMonth(month);
    setAnnouncement(monthLabel(year, month));
  }

  function stepMonthBy(delta: number) {
    const next = stepMonth(viewYear, viewMonth, delta);
    goToMonth(next.year, next.month);
    // Keep focusedDate inside the newly-visible month (same day-of-month,
    // clamped to the shorter month's length if needed) so a real, currently
    // rendered cell always exists to receive DOM focus. Without this, the
    // previously-focused cell unmounts when the grid re-renders for the new
    // month, keyboard focus is dropped to nothing, and a second consecutive
    // PageUp/PageDown/chevron keypress has no element to fire on — the bug
    // this comment guards against (caught by a repeated-PageUp test).
    const daysInNextMonth = new Date(Date.UTC(next.year, next.month + 1, 0)).getUTCDate();
    const { day } = parseIsoDate(focusedDate);
    setFocusedDate(toIsoDate(next.year, next.month, Math.min(day, daysInNextMonth)));
  }

  function moveFocusBy(days: number) {
    const next = addDays(focusedDate, days);
    const { year, month } = parseIsoDate(next);
    if (year !== viewYear || month !== viewMonth) {
      goToMonth(year, month);
    }
    setFocusedDate(next);
  }

  function handleGridKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        moveFocusBy(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        moveFocusBy(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveFocusBy(-7);
        break;
      case 'ArrowDown':
        e.preventDefault();
        moveFocusBy(7);
        break;
      case 'Home': {
        e.preventDefault();
        const first = toIsoDate(viewYear, viewMonth, 1);
        setFocusedDate(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
        setFocusedDate(toIsoDate(viewYear, viewMonth, daysInMonth));
        break;
      }
      case 'PageUp':
        e.preventDefault();
        stepMonthBy(-1);
        break;
      case 'PageDown':
        e.preventDefault();
        stepMonthBy(1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(focusedDate);
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  }

  // Keeps DOM focus in sync with focusedDate for keyboard grid navigation,
  // and — since this also runs on mount with focusedDate already initialized
  // to selected ?? today — is what satisfies SS5.5's "opening the popover
  // moves focus to the currently-selected date's cell (or today's cell if
  // nothing selected yet) — not to the first cell of the grid."
  useEffect(() => {
    const el = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-date="${focusedDate}"]`,
    );
    el?.focus();
  }, [focusedDate]);

  // Bug-fix guard (today-phone-parity.md §5.1, flagged risk): the scrim
  // below is a full-viewport, ALWAYS-PRESENT `<div onClick={onClose}>`
  // mounted in the same render that opens this popover. When this component
  // is opened from a trigger that is itself the result of a PRIOR click in
  // the same stacked-popover flow — the phone footer's "tap the menu
  // trigger, then tap 'Backfill a day'" sequence — a real gesture's trailing
  // events (mouseup/click firing after the state commit that just mounted
  // this scrim) can land on the scrim and close it the instant it opens.
  // This is the exact "opens then immediately closes within the same
  // interaction" race `TodayFooterMenu` (TodayView.tsx) already documented
  // and fixed once for its own outside-click listener, by registering that
  // listener on the NEXT tick rather than synchronously during the opening
  // click's own event-handling phase. Applied here identically: the scrim
  // element stays mounted (so it's still visually/semantically a scrim, and
  // desktop's existing single-trigger callers — TodayHeaderDesktopV2,
  // ObjectiveFormScreen, ActionsTable — see no behavior change, since a
  // single click can't hit both the trigger AND this scrim in one gesture),
  // but its click handler is a no-op until one tick after mount.
  const [scrimArmed, setScrimArmed] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setScrimArmed(true), 0);
    return () => window.clearTimeout(id);
  }, []);

  const anchorSide: React.CSSProperties =
    anchor === 'above'
      ? { bottom: 'calc(100% + var(--space-2))' }
      : { top: 'calc(100% + var(--space-2))' };

  return (
    <>
      {/* Scrim: outside tap closes, keeping whatever was last selected (SS5.5). */}
      <div
        onClick={() => {
          if (scrimArmed) onClose();
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
          background: 'transparent',
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Choose target date"
        tabIndex={-1}
        style={{
          position: 'absolute',
          zIndex: 41,
          ...anchorSide,
          left: 0,
          right: 0,
          background: 'var(--surface-overlay)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-2)',
          padding: 'var(--space-4)',
        }}
      >
        <div
          aria-live="polite"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {announcement}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <IconStepButton
            direction="left"
            label="Previous month"
            onClick={() => stepMonthBy(-1)}
          />
          <span
            role="heading"
            aria-level={2}
            className="num"
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            {monthLabel(viewYear, viewMonth)}
          </span>
          <IconStepButton
            direction="right"
            label="Next month"
            onClick={() => stepMonthBy(1)}
          />
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            marginTop: 'var(--space-3)',
          }}
        >
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              style={{
                fontSize: 'var(--type-caption-size)',
                color: 'var(--text-muted)',
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              {label}
            </div>
          ))}
        </div>

        <div
          ref={gridRef}
          role="grid"
          aria-label={monthLabel(viewYear, viewMonth)}
          onKeyDown={handleGridKeyDown}
        >
          {weeks.map((week, i) => (
            <div key={i} role="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {week.map((cell) => (
                <DateCell
                  key={cell.date}
                  cell={cell}
                  today={today}
                  selected={selected}
                  isFocusTarget={cell.date === focusedDate}
                  onSelect={() => onSelect(cell.date)}
                  onFocus={() => setFocusedDate(cell.date)}
                />
              ))}
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 'var(--space-3)',
          }}
        >
          <button
            type="button"
            onClick={() => {
              goToMonth(parseIsoDate(today).year, parseIsoDate(today).month);
              onSelect(today);
            }}
            style={{
              color: 'var(--hue-primary)',
              fontSize: 'var(--type-caption-size)',
              minHeight: 'var(--touch-target-min)',
            }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              color: 'var(--hue-primary)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              minHeight: 'var(--touch-target-min)',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}

function IconStepButton({
  direction,
  label,
  onClick,
}: {
  readonly direction: 'left' | 'right';
  readonly label: string;
  readonly onClick: () => void;
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        width: 'var(--touch-target-min)',
        height: 'var(--touch-target-min)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={16} color="var(--text-muted)" aria-hidden="true" />
    </button>
  );
}

function DateCell({
  cell,
  today,
  selected,
  isFocusTarget,
  onSelect,
  onFocus,
}: {
  readonly cell: CalendarCell;
  readonly today: string;
  readonly selected: string | null;
  readonly isFocusTarget: boolean;
  readonly onSelect: () => void;
  readonly onFocus: () => void;
}) {
  const isToday = cell.date === today;
  const isSelected = selected !== null && cell.date === selected;
  const isPast = compareDates(cell.date, today) < 0;

  // Bug fix (caught by inspecting the live rendered page, not by component
  // tests — jsdom doesn't lay out real flexbox/grid sizing): an earlier
  // version gave the button/filler a fixed 36px box inside a
  // `justify-content: center` wrapper. Each grid column is only
  // `available-width / 7` wide (~44px on a 390pt phone frame after page
  // margins and popover padding, but as little as ~39px in a narrower
  // context) — a fixed 36-44px box centered in a variable-width column
  // either wastes space or, worse, overflows the popover's own right edge
  // once enough columns compound a few px of overflow each. Both cell
  // variants now fill 100% of their grid column/row instead of claiming a
  // fixed pixel size, so the button/filler IS the touch target at whatever
  // size CSS Grid actually gives that column — correct at any viewport
  // width, and naturally close to touch.targetMin (44px) at the product's
  // real phone-frame width, without ever exceeding the available track.
  if (!cell.inCurrentMonth) {
    // Outside-month filler: visible for grid-shape continuity but not
    // focusable/tappable — a disabled button (not aria-hidden, since it's
    // visibly on screen) so it's skipped in tab order (SS5.3).
    return (
      <button
        type="button"
        disabled
        tabIndex={-1}
        className="num"
        style={{
          width: '100%',
          aspectRatio: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--radius-pill)',
          fontSize: 'var(--type-body-size)',
          color: 'var(--text-disabled)',
        }}
      >
        {cell.day}
      </button>
    );
  }

  return (
    <button
      type="button"
      role="gridcell"
      data-date={cell.date}
      tabIndex={isFocusTarget ? 0 : -1}
      aria-selected={isSelected}
      aria-current={isToday ? 'date' : undefined}
      aria-label={formatLongDate(cell.date)}
      onClick={onSelect}
      onFocus={onFocus}
      className="date-cell-hoverable"
      style={{
        width: '100%',
        aspectRatio: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Visible painted circle, slightly inset from the full grid-cell
          button so adjacent cells don't visually touch — a spec-compliant
          "smaller visible circle inside a larger tap target" without a
          fixed pixel size that could overflow. */}
      <span
        aria-hidden="true"
        className="num date-cell-fill"
        style={{
          width: '82%',
          height: '82%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 'var(--radius-pill)',
          fontSize: 'var(--type-body-size)',
          background: isSelected ? 'var(--hue-primary-vivid)' : 'transparent',
          color: isSelected
            ? 'var(--text-on-accent)'
            : isPast
              ? 'var(--text-muted)'
              : 'var(--text-primary)',
          border: isToday && !isSelected ? '1.5px solid var(--hue-primary)' : 'none',
        }}
      >
        {cell.day}
      </span>
    </button>
  );
}
